import React, { useEffect, useState } from "react";
import { FaTimes, FaBell, FaUserMd, FaCalendarAlt, FaExclamationTriangle, FaCheckCircle, FaTrash, FaClock, FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { getAllNotifications, getTodayNotifications, getRecentNotifications, markAllNotificationsRead, clearAllNotifications, markNotificationRead, clearNotification } from "../../../lib/apis/notifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import { getCookie } from "../../../lib/utils/cookies";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export interface Notification {
  notification_id: string;
  body: string;
  created_at: string;
  type?: 'appointment' | 'doctor' | 'alert' | 'info';
  priority?: 'high' | 'medium' | 'low';
  read?: boolean;
  is_read: boolean;
  metadata?: {
    doctor_id?: string;
    patient_id?: string;
    booking_id?: string;
    doctorName?: string;
    patient_name?: string;
    mapping_id?: string;
  };
}

interface NotificationsProps {
  isOpen: boolean;
  onClose: () => void;
  onFetched?: (notifications: Notification[]) => void;
  userId: string | undefined;
  onStateChange?: () => void;
}

const Notifications = ({ isOpen, onClose, onFetched, userId, onStateChange }: NotificationsProps) => {
  const [todayNotifications, setTodayNotifications] = useState<Notification[]>([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'today' | 'all'>('today');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    const muted = localStorage.getItem('notifications_muted');
    return muted === 'true';
  });
  const timezone = getCookie("timezone") || "UTC";
  const navigate = useNavigate();
  const {
    hospitalID
  } = useHospDocData();

  // Use the correct list based on filter
  const notifications = filter === 'today' ? todayNotifications : allNotifications;

  // Helper to get local time from UTC
  const getLocalTime = (utcTime: string) => moment.utc(utcTime).tz(timezone);

  const handleMuteToggle = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    localStorage.setItem('notifications_muted', newMutedState.toString());
  };

  const fetchAllNotificationLists = async () => {
    setLoading(true);
    setError(null);
    try {
      const [todayRes, allRes] = await Promise.all([
        getTodayNotifications(userId || ""),
        getAllNotifications(userId || "")
      ]);
      setTodayNotifications(Array.isArray(todayRes) ? todayRes : []);
      setAllNotifications(Array.isArray(allRes) ? allRes : []);
      if (onFetched) onFetched(filter === 'today' ? todayRes : allRes);
    } catch (err) {
      setError("Failed to fetch notifications.");
      setTodayNotifications([]);
      setAllNotifications([]);
      if (onFetched) onFetched([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAllNotificationLists();
    }
    // eslint-disable-next-line
  }, [isOpen, userId]);

  // When filter changes, call onFetched with the correct list
  useEffect(() => {
    if (onFetched) onFetched(filter === 'today' ? todayNotifications : allNotifications);
    // eslint-disable-next-line
  }, [filter, todayNotifications, allNotifications]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleClearAll = async () => {
    setLoading(true);
    try {
      if (filter === 'all') {
        if (userId) {
          await Promise.all(allNotifications.map(n => clearNotification(n.notification_id)));
        }
      } else {
        // First, mark unread ones as read (if needed)
        const unread = todayNotifications.filter(n => !n.is_read);
        await Promise.all(unread.map(n => markNotificationRead(n.notification_id)));
        // Then, delete all
        await Promise.all(todayNotifications.map(n => clearNotification(n.notification_id)));
      }
      setShowClearConfirm(false);
      await fetchAllNotificationLists();
      if (onStateChange) onStateChange();
    } catch (err) {
      console.error("Clear all failed", err);
      setError("Failed to clear notifications.");
    } finally {
      setLoading(false);
    }
  };
  
  

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      const targetList = filter === 'today' ? todayNotifications : allNotifications;
      const unreadNotifications = targetList.filter(n => !n.is_read);
      await Promise.all(unreadNotifications.map(n => markNotificationRead(n.notification_id)));
      fetchAllNotificationLists();
      if (onStateChange) onStateChange();
    } catch {
      setError("Failed to mark all as read.");
    } finally {
      setLoading(false);
    }
  };
  

  const handleNotificationClick = async (notification: Notification) => {
    console.log(notification);
    if (!notification.is_read) {
      await markNotificationRead(notification.notification_id);
      // Refresh both lists
      fetchAllNotificationLists();
      if (onStateChange) onStateChange();
    }
    // Navigate to appointment search if metadata is present
    const patient_name = notification.metadata?.patient_name;
    if (hospitalID && patient_name) {
      if(notification.body.includes("Appointment has been cancelled")){
        navigate(`/${hospitalID}/appointments?type=cancelled&search=${encodeURIComponent(patient_name)}`);
      }else{
        navigate(`/${hospitalID}/appointments?type=all&search=${encodeURIComponent(patient_name)}`);
      }
    } else if(hospitalID && notification.metadata?.mapping_id){
      navigate(`/${hospitalID}/doctors/doc/${encodeURIComponent(notification.metadata?.mapping_id)}?tab=1`);
    }else{
      navigate(`/${hospitalID}/appointments?type=all`);
    }
    onClose();
    // Optionally, do more (e.g., open a detail view)
  };

  return (
    <>
      <div className={`fixed inset-0 z-50 transition-all duration-300 backdrop-blur-sm ease-out ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        {/* Professional Backdrop */}
        <div
          className={`fixed inset-0 bg-slate-900/20 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
        />

        {/* Professional Sidebar */}
        <div className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>

          {/* Professional Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="px-6 py-5 border-b border-slate-600/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/20">
                      <FaBell className="w-5 h-5 text-white" />
                    </div>
                    {/* {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-medium px-1.5 shadow-lg animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )} */}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Notifications</h2>
                    {/* <p className="text-slate-300 text-sm mt-0.5">Healthcare Management System</p> */}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleMuteToggle}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
                    title={isMuted ? "Unmute notifications" : "Mute notifications"}
                  >
                    {isMuted ? (
                      <FaVolumeMute className="w-5 h-5" />
                    ) : (
                      <FaVolumeUp className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Professional Filter Tabs */}
            <div className="px-6 py-4">
              <div className="flex bg-slate-700/30 rounded-xl p-1 backdrop-blur-sm">
                <button
                  onClick={() => setFilter('today')}
                  className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${filter === 'today'
                      ? 'bg-white text-slate-700 shadow-md'
                      : 'text-slate-200 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <FaClock className="w-3.5 h-3.5" />
                    Today ({todayNotifications.length})
                  </div>
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${filter === 'all'
                      ? 'bg-white text-slate-700 shadow-md'
                      : 'text-slate-200 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <FaBell className="w-3.5 h-3.5" />
                    All ({allNotifications.length})
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Content Body */}
          <div className="flex-1 overflow-y-auto bg-slate-50 side_bar">
            {loading && (
              <div className="p-8 text-center">
                <div className="w-12 h-12 border-3 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600 font-medium">Loading notifications...</p>
              </div>
            )}

            {error && !loading && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExclamationTriangle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Connection Error</h3>
                <p className="text-slate-600 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && notifications.length === 0 && (
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaBell className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  {filter === 'today' ? 'No notifications today' : 'All notifications viewed'}
                </h3>
                <p className="text-slate-500 text-sm">
                  {filter === 'today'
                    ? 'You have no new notifications for today.'
                    : 'You\'re all caught up with recent notifications.'
                  }
                </p>
              </div>
            )}

            {!loading && !error && notifications.length > 0 && (
              <div className="divide-y divide-slate-200">
                {[...notifications]
                  .sort((a, b) => Number(a.is_read) - Number(b.is_read))
                  .map((notification, index) => (
                    <div
                      key={notification.notification_id}
                      className="animate-fadeInUp"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <ProfessionalNotificationItem
                        notification={notification}
                        onClick={handleNotificationClick}
                        getLocalTime={getLocalTime}
                        timezone={timezone}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Professional Footer */}
          {notifications.length > 0 && (
            <div className="p-4 bg-white border-t border-slate-200">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                  Clear All
                </button>
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                >
                  Mark All as Read
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Professional Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowClearConfirm(false)}></div>
          <div className="relative bg-white rounded-xl p-6 shadow-xl max-w-md w-full border border-slate-200">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaExclamationTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Clear All Notifications</h3>
              <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                Are you sure you want to clear all notifications? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearAll}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

// Professional NotificationItem component
const ProfessionalNotificationItem = ({ notification, onClick, getLocalTime, timezone }: { notification: Notification, onClick: (notification: Notification) => void, getLocalTime: (utcTime: string) => moment.Moment, timezone: string }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getIcon = () => {
    const iconClass = "w-5 h-5";
    switch (notification.type) {
      case 'doctor':
        return <FaUserMd className={`${iconClass} text-blue-600`} />;
      case 'appointment':
        return <FaCalendarAlt className={`${iconClass} text-green-600`} />;
      case 'alert':
        return <FaExclamationTriangle className={`${iconClass} text-amber-600`} />;
      default:
        return <FaCheckCircle className={`${iconClass} text-slate-600`} />;
    }
  };

  const getPriorityIndicator = () => {
    switch (notification.priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50/50';
      case 'medium':
        return 'border-l-amber-500 bg-amber-50/50';
      default:
        return 'border-l-blue-500 bg-blue-50/50';
    }
  };

  const getTypeLabel = () => {
    switch (notification.type) {
      case 'doctor':
        return 'Doctor Update';
      case 'appointment':
        return 'Appointment';
      case 'alert':
        return 'Alert';
      default:
        return 'Notification';
    }
  };

  const isRecent = getLocalTime(notification.created_at).isSame(moment.tz(timezone), 'day');
  const timeAgo = getLocalTime(notification.created_at).fromNow();

  return (
    <div
      className={`p-4 hover:bg-slate-50 transition-colors duration-200 cursor-pointer border-l-4 ${getPriorityIndicator()
        } ${notification.is_read ? 'border-l-slate-400 bg-white' : 'bg-slate-50/50'
        }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(notification)}
      style={{ cursor: "pointer" }}
    >
      <div className="flex items-start gap-4">
        {/* Professional Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${!notification.is_read ? 'bg-white shadow-sm border border-slate-200' : 'bg-slate-100 border border-slate-200'
          } ${isHovered ? 'shadow-md' : ''}`}>
          {getIcon()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              {/* Type Label */}
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${notification.type === 'doctor' ? 'bg-blue-100 text-blue-700' :
                    notification.type === 'appointment' ? 'bg-green-100 text-green-700' :
                      notification.type === 'alert' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                  }`}>
                  {getTypeLabel()}
                </span>
                {notification.priority === 'high' && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    High Priority
                  </span>
                )}
                {isRecent && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    New
                  </span>
                )}
              </div>

              {/* Notification Content */}
              <p className={`text-sm leading-relaxed mb-2 ${!notification.is_read
                  ? 'font-medium text-slate-900'
                  : 'text-slate-700'
                }`}>
                {notification.body}
              </p>

              {/* Time Information */}
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <FaClock className="w-3 h-3" />
                <span>{timeAgo}</span>
                <span className="text-slate-300">•</span>
                <span>{getLocalTime(notification.created_at).format('MMM DD, YYYY h:mm A')}</span>
              </div>
            </div>

            {/* Unread Indicator */}
            {!notification.is_read && (
              <div className="w-2.5 h-2.5 bg-blue-600 rounded-full mt-1 flex-shrink-0"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;